import { useQuery } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'

import { useApiClient } from '@/api/ApiClientProvider'
import { Badge, Card, EmptyState } from '@/design'

import { INTEGRATION_PROVIDERS, providerFor, type IntegrationProvider } from './providers'
import { integrationKeys } from './queryKeys'
import './integrations.css'
import { WebsiteHealthCards } from './websites/WebsiteHealthCards'

/**
 * Integrations: each website's connector health, then one card per provider in
 * the registry, with its state at a glance and a way in. Owner and
 * Administrator only (`integrations.manage`).
 */
export function IntegrationsPage() {
  return (
    <>
      <WebsiteHealthCards />
      <ul className="ci-integrations" aria-label="Integrations">
        {INTEGRATION_PROVIDERS.map((provider) => (
          <li key={provider.key}>
            <ProviderCard provider={provider} />
          </li>
        ))}
      </ul>
    </>
  )
}

function ProviderCard({ provider }: { provider: IntegrationProvider }) {
  const client = useApiClient()
  const summary = useQuery({
    queryKey: integrationKeys.summary(provider.key, client.kind),
    queryFn: () => (provider.summary ? provider.summary(client) : Promise.resolve(null)),
  })
  return (
    <Card
      title={provider.label}
      actions={summary.data ? <Badge tone={summary.data.tone}>{summary.data.label}</Badge> : null}
    >
      <p className="ci-integrations__description">{provider.description}</p>
      <Link className="ci-button" to={provider.route}>
        Manage {provider.label}
      </Link>
    </Card>
  )
}

/** `/integrations/:provider`: the registry entry's own page, or a clear dead end. */
export function IntegrationProviderPage() {
  const { provider: key } = useParams()
  const provider = providerFor(key)
  if (!provider) {
    return (
      <EmptyState
        variant="no-data"
        title="No such integration"
        description="Choose an integration from the Integrations page."
      />
    )
  }
  const { Detail } = provider
  return <Detail />
}
